import { FC, useEffect, useRef, useState } from "react";
import { formatDistanceStrict } from "date-fns";
import { useTranslation } from "react-i18next";
import { IOrder, IOrderEvent } from "../types";
import { OrderTimelineItem } from "./OrderTimelineItem";
import { H3 } from "../../../components/Typography";
import styles from "./OrderTimeline.module.css";

interface Props {
  order: IOrder;
}

export interface IOrderEventModelExtended extends IOrderEvent {
  difference: string;
  span: number;
}

const extend = (items: IOrderEvent[]): IOrderEventModelExtended[] => {
  return items.map((item, index) => {
    const prev = items[index - 1];
    if (prev) {
      return {
        ...item,
        difference: formatDistanceStrict(
          new Date(prev.eventTime),
          new Date(item.eventTime)
        ),
        span: 0,
      };
    }
    return {
      ...item,
      difference: "",
      span: 0,
    };
  });
};

export const OrderTimeline: FC<Props> = ({ order }) => {
  const { t } = useTranslation("order");
  const rightContentRef = useRef<HTMLDivElement>(null);
  const [dividerHeight, setDividerHeight] = useState<number>(0);

  const timelineExtended: IOrderEventModelExtended[] = extend(order.events);

  useEffect(() => {
    if (rightContentRef.current) {
      setDividerHeight(rightContentRef.current.scrollHeight);
    }
  }, [timelineExtended]);

  return (
    <div className={styles.container}>
      <H3 className={styles.title}>{t("timeline")}</H3>
      <div className={styles.content}>
        <div className={styles["divider-container"]}>
          <div
            className={styles.divider}
            style={{ height: dividerHeight - 50 }}
          />
        </div>
        <div className={styles["right-container"]} ref={rightContentRef}>
          {timelineExtended.map((timelineItem, index) => (
            <OrderTimelineItem
              key={timelineItem.eventTime}
              item={timelineItem}
              isFirst={index === 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
